import React, { useEffect, useState } from 'react';
import { inventoryMetaApi } from '../../services/api';

const DEFAULT_FORM = {
    name: '',
    sku: '',
    barcode: '',
    category: '',
    sub_category: '',
    brand: '',
    unit: 'Pc',
    warranty: '',
    unit_cost: '',
    selling_price: '',
    quantity_in_stock: '',
    reorder_level: '',
    batch_number: '',
    expiry_date: '',
    description: '',
};

function makeBarcode() {
    return String(Date.now()).slice(-12);
}

export default function ProductForm({ product, saving, onSubmit, submitLabel }) {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [options, setOptions] = useState({});
    const [images, setImages] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState({});
    const isEdit = !!(product?.id);

    useEffect(() => {
        let mounted = true;
        inventoryMetaApi.options().then((response) => {
            if (!mounted || !response.success) return;
            setOptions(response.data || {});
        });

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        setForm({
            ...DEFAULT_FORM,
            ...(product || {}),
            sku: product?.sku || '',
            barcode: product?.barcode || '',
            sub_category: product?.metadata?.sub_category || product?.metadata?.subCategory || '',
            brand: product?.metadata?.brand || product?.brand || '',
            unit: product?.metadata?.unit || product?.unit || 'Pc',
            warranty: product?.metadata?.warranty || '',
            unit_cost: product?.unit_cost ?? '',
            selling_price: product?.selling_price ?? '',
            quantity_in_stock: product?.quantity_in_stock ?? '',
            reorder_level: product?.reorder_level ?? '',
            batch_number: product?.batch_number || '',
            expiry_date: product?.expiry_date ? String(product.expiry_date).slice(0, 10) : '',
        });
        const variantMap = {};
        const existingVariants = Array.isArray(product?.metadata?.variants) ? product.metadata.variants : [];
        existingVariants.forEach((variant) => {
            if (variant?.name) variantMap[variant.name] = Array.isArray(variant.values) ? variant.values : [];
        });
        setSelectedVariants(variantMap);
        // Populate images from metadata.images or fallback to image_url
        const existingImages = Array.isArray(product?.metadata?.images)
            ? product.metadata.images
            : product?.image_url
                ? [product.image_url]
                : [];
        setImages(existingImages);
    }, [product]);

    const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const onImageFiles = (fileList) => {
        if (!fileList || fileList.length === 0) return;
        const files = Array.from(fileList);
        Promise.all(
            files.map(
                (file) =>
                    new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(String(reader.result || ''));
                        reader.readAsDataURL(file);
                    })
            )
        ).then((dataUrls) => setImages((prev) => [...prev, ...dataUrls]));
    };

    const removeImage = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    const toggleVariantValue = (variantName, value) => {
        setSelectedVariants((prev) => {
            const current = prev[variantName] || [];
            const nextValues = current.includes(value)
                ? current.filter((item) => item !== value)
                : [...current, value];
            const next = { ...prev, [variantName]: nextValues };
            if (nextValues.length === 0) delete next[variantName];
            return next;
        });
    };

    const submit = (e) => {
        e.preventDefault();
        const variants = Object.entries(selectedVariants)
            .map(([name, values]) => ({ name, values }))
            .filter((item) => item.values.length > 0);
        onSubmit({
            ...form,
            unit_cost: Number(form.unit_cost || 0),
            selling_price: Number(form.selling_price || 0),
            quantity_in_stock: Number(form.quantity_in_stock || 0),
            reorder_level: Number(form.reorder_level || 10),
            batch_number: form.batch_number,
            expiry_date: form.expiry_date || null,
            image_url: images[0] || '',
            metadata: {
                ...(product?.metadata || {}),
                brand: form.brand,
                unit: form.unit,
                sub_category: form.sub_category,
                warranty: form.warranty,
                images,
                variants,
            },
        });
    };

    const categoryOptions = options.category || [];
    const subCategoryOptions = options.sub_category || [];
    const brandOptions = options.brand || [];
    const unitOptions = options.unit || [];
    const warrantyOptions = options.warranty || [];
    const variantOptions = options.variant || [];

    return (
        <form className="modal-form product-form-page" onSubmit={submit}>
            <div className="modal-grid modal-grid-2">
                <label>
                    <span>Product Name</span>
                    <input value={form.name} onChange={(e) => update('name', e.target.value)} required />
                </label>
                <label>
                    <span>Category</span>
                    <select value={form.category} onChange={(e) => update('category', e.target.value)} required>
                        <option value="">Select category</option>
                        {categoryOptions.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                    </select>
                </label>
                <label>
                    <span>Sub Category</span>
                    <select value={form.sub_category} onChange={(e) => update('sub_category', e.target.value)}>
                        <option value="">Select sub category</option>
                        {subCategoryOptions
                            .filter((item) => !form.category || item.parent_name === form.category)
                            .map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                    </select>
                </label>
                <label>
                    <span>Brand</span>
                    <select value={form.brand} onChange={(e) => update('brand', e.target.value)}>
                        <option value="">Select brand</option>
                        {brandOptions.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                    </select>
                </label>
                <label>
                    <span>Unit</span>
                    <select value={form.unit} onChange={(e) => update('unit', e.target.value)}>
                        <option value="">Select unit</option>
                        {unitOptions.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                    </select>
                </label>
                <label>
                    <span>Warranty</span>
                    <select value={form.warranty} onChange={(e) => update('warranty', e.target.value)}>
                        <option value="">Select warranty</option>
                        {warrantyOptions.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                    </select>
                </label>
                <label>
                    <span>SKU</span>
                    <input value={form.sku} onChange={(e) => update('sku', e.target.value)} placeholder="Auto if empty" />
                </label>
                <label>
                    <span>Barcode</span>
                    <div className="barcode-input-row">
                        <input
                            value={form.barcode}
                            onChange={(e) => update('barcode', e.target.value)}
                            placeholder="Scan or auto-generate"
                            readOnly={isEdit}
                            style={isEdit ? { background: 'var(--bg)', cursor: 'not-allowed', color: 'var(--muted)' } : undefined}
                        />
                        {!isEdit && (
                            <button className="btn btn-outline" type="button" onClick={() => update('barcode', makeBarcode())}>Generate</button>
                        )}
                    </div>
                    <small className="field-hint">{isEdit ? 'Barcode cannot be changed after creation.' : 'The cashier can scan this barcode from the POS search box.'}</small>
                </label>
                <label>
                    <span>Unit Cost</span>
                    <input type="number" min="0" step="0.01" value={form.unit_cost} onChange={(e) => update('unit_cost', e.target.value)} required />
                </label>
                <label>
                    <span>Selling Price</span>
                    <input type="number" min="0" step="0.01" value={form.selling_price} onChange={(e) => update('selling_price', e.target.value)} required />
                </label>
                <label>
                    <span>Quantity</span>
                    <input type="number" min="0" step="1" value={form.quantity_in_stock} onChange={(e) => update('quantity_in_stock', e.target.value)} required />
                </label>
                <label>
                    <span>Reorder Level</span>
                    <input type="number" min="0" step="1" value={form.reorder_level} onChange={(e) => update('reorder_level', e.target.value)} required />
                </label>
                <label>
                    <span>Batch Number</span>
                    <input value={form.batch_number} onChange={(e) => update('batch_number', e.target.value)} placeholder="Optional batch or lot number" />
                </label>
                <label>
                    <span>Expiry Date</span>
                    <input type="date" value={form.expiry_date} onChange={(e) => update('expiry_date', e.target.value)} />
                    <small className="field-hint">Used by the portal to calculate expired products.</small>
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                    <span>Product Images (select multiple)</span>
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => onImageFiles(e.target.files)}
                    />
                    <small className="field-hint">Hold Ctrl/Cmd to select multiple files at once.</small>
                </label>
            </div>

            {variantOptions.length > 0 ? (
                <section className="product-variant-builder">
                    <div>
                        <p className="barcode-preview-label">Variant Attributes</p>
                        <small className="field-hint">Select the options customers can choose before adding this product to an order.</small>
                    </div>
                    <div className="product-variant-grid">
                        {variantOptions.map((variant) => {
                            const values = Array.isArray(variant.values) ? variant.values : [];
                            if (!values.length) return null;
                            return (
                                <div className="product-variant-group" key={variant.id || variant.name}>
                                    <strong>{variant.name}</strong>
                                    <div>
                                        {values.map((value) => (
                                            <button
                                                key={`${variant.name}-${value}`}
                                                type="button"
                                                className={`variant-value-chip ${(selectedVariants[variant.name] || []).includes(value) ? 'active' : ''}`}
                                                onClick={() => toggleVariantValue(variant.name, value)}
                                            >
                                                {value}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            ) : null}
            <label>
                <span>Description</span>
                <textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={4} />
            </label>

            {images.length > 0 ? (
                <div style={{ marginTop: 10 }}>
                    <p className="barcode-preview-label" style={{ marginBottom: 8 }}>Product Images ({images.length})</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {images.map((src, idx) => (
                            <div key={idx} style={{ position: 'relative' }}>
                                <img
                                    src={src}
                                    alt={`Product ${idx + 1}`}
                                    style={{ height: 64, width: 64, objectFit: 'cover', borderRadius: 8, border: idx === 0 ? '2px solid var(--primary)' : '1px solid var(--line)' }}
                                />
                                {idx === 0 && (
                                    <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 9, background: 'var(--primary)', color: '#fff', borderRadius: 4, padding: '1px 4px' }}>Main</span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => removeImage(idx)}
                                    style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                >✕</button>
                            </div>
                        ))}
                    </div>
                    <small className="field-hint">First image is the main product image. Click ✕ to remove.</small>
                </div>
            ) : null}

            <div className="barcode-preview-box">
                <div>
                    <p className="barcode-preview-label">Live Barcode</p>
                    <strong>{form.barcode || 'Auto-generated on save'}</strong>
                </div>
                <div className="barcode-preview-bars" aria-hidden="true">
                    {Array.from({ length: 28 }).map((_, index) => (
                        <span key={index} style={{ height: `${16 + ((index * 7) % 24)}px` }} />
                    ))}
                </div>
            </div>

            <div className="modal-footer">
                <button type="submit" className="btn" disabled={saving}>{saving ? 'Saving...' : submitLabel}</button>
            </div>
        </form>
    );
}
